/**
 * Capacitor StatusBar wrapper — Phase 1, incrément 20.
 *
 * Push la palette courante (vert sauge classique / bleu ardoise
 * coparenting) vers la barre de statut native iOS/Android quand
 * l'app tourne en Capacitor. No-op en web.
 *
 * Cf. proto l. 1048 (`updateStatusBarColor`) + l. 3765-3771 pour le
 * contrat StatusBar Capacitor :
 *   - `setStyle({ style: 'LIGHT' | 'DARK' })` (texte clair/sombre)
 *   - `setBackgroundColor({ color: '#7A9E8E' })` (hex string)
 *   - `show()` (s'assurer que la status bar est visible)
 */
import { getPlugin } from './index.js';

/** Surface du plugin StatusBar Capacitor (subset utilisé). */
interface StatusBarPlugin {
  setStyle(opts: { style: 'LIGHT' | 'DARK' | 'DEFAULT' }): Promise<void>;
  setBackgroundColor(opts: { color: string }): Promise<void>;
  show(): Promise<void>;
  hide(): Promise<void>;
}

/**
 * Pousse la couleur de fond + style texte vers la status bar native.
 *
 * @param hex - Couleur hex (`#7A9E8E` ou `#5B6C8F`).
 * @param textStyle - `'LIGHT'` pour texte blanc (par défaut, palette
 *   sombre Nestor), `'DARK'` pour texte noir.
 *
 * No-op si Capacitor StatusBar n'est pas disponible (web). Ne throw
 * jamais — les erreurs natives sont swallowées et loggées en dev.
 */
export async function setStatusBarColor(
  hex: string,
  textStyle: 'LIGHT' | 'DARK' = 'LIGHT',
): Promise<boolean> {
  const plugin = getPlugin<StatusBarPlugin>('StatusBar');
  if (!plugin) return false;
  try {
    await plugin.setBackgroundColor({ color: hex });
    await plugin.setStyle({ style: textStyle });
    return true;
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[capacitor:StatusBar] setStatusBarColor failed:', err);
    }
    return false;
  }
}

/** Affiche la status bar (utile après un fullscreen). */
export async function showStatusBar(): Promise<boolean> {
  const plugin = getPlugin<StatusBarPlugin>('StatusBar');
  if (!plugin) return false;
  try {
    await plugin.show();
    return true;
  } catch {
    return false;
  }
}

/** Masque la status bar (player vidéo plein écran, par ex.). */
export async function hideStatusBar(): Promise<boolean> {
  const plugin = getPlugin<StatusBarPlugin>('StatusBar');
  if (!plugin) return false;
  try {
    await plugin.hide();
    return true;
  } catch {
    return false;
  }
}
